import { toast } from "sonner";
import { useTranslations } from "use-intl";
import { z } from "zod";
import { ShieldExclamationIcon } from "@heroicons/react/20/solid";
import { Currency } from "@/gql/graphql";
import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { openIncidentClaimMutation } from "@/app/Incidents/GraphQL/openIncidentClaimMutation";
import { IncidentClaimFields } from "@/app/Incidents/Components/IncidentClaimFields";
import {
  incidentClaimShape,
  requireIncidentClaim,
} from "@/app/Incidents/Forms/incidentClaimSchema";

const openClaimSchema = z.object(incidentClaimShape);

type OpenClaimFormValues = z.infer<typeof openClaimSchema>;

export function OpenIncidentClaimDialog({
  incidentId,
  currency,
}: {
  incidentId: string;
  currency: Currency;
}) {
  const t = useTranslations("incident");
  const { close } = useDialogState();
  const { mutateAsync } = useGqlMutation(openIncidentClaimMutation);

  const schema = openClaimSchema.superRefine(
    requireIncidentClaim({
      excessRequired: t("claim.excessRequired"),
      documentsRequired: t("claim.documentsRequired"),
    }),
  );

  return (
    <BaseDialog
      title={t("claimDialog.title")}
      subtitle={t("claimDialog.subtitle")}
      size="3xl"
    >
      <GenericForm<OpenClaimFormValues>
        initialValues={{
          excess_amount: null,
          insurer: "",
          claim_reference: "",
          claim_documents: [],
        }}
        validationSchema={toFormikValidationSchema(schema)}
        onSubmit={async (values) => {
          await mutateAsync({
            id: incidentId,
            input: {
              excess_amount: values.excess_amount ?? 0,
              insurer: values.insurer || null,
              claim_reference: values.claim_reference || null,
              document_file_paths: values.claim_documents.map(
                (upload) => upload.filePath,
              ),
            },
          });

          toast.success(t("claimDialog.success"));
          await close();
        }}
      >
        <IncidentClaimFields currency={currency} />
        <DialogSubmitButton
          text={t("claimDialog.submit")}
          Icon={ShieldExclamationIcon}
        />
      </GenericForm>
    </BaseDialog>
  );
}
